import * as mo from "mori";
import * as _ from "underscore";
import { kde } from "../charts/kde";
import { memoize } from "../lib/memoize";
import * as Numbers from "../lib/Numbers";
import { I18nContext } from "./i18n";

// -----------------------------------------------------------------------------

export type CoreDimKey = "benefit" | "social_cohesion" | "quality_of_life" | "ethics";

export type SummaryDimKey = "public_value";

export type DimKey = CoreDimKey | SummaryDimKey;

export function localizeDim(I18n: I18nContext) {
  return (x: { key: DimKey }) => {
    return { ...x, name: I18n.t(`frontend.dimensions.${x.key}`) };
  };
}

// -----------------------------------------------------------------------------
// Dimension

export interface DimensionValues {
  readonly key: DimKey;
  readonly values: number[];
  readonly avg: number;
  readonly ci: [number, number];
  readonly kdeData: number[][]; // type KdeData = …
}

export const mkDimensionValues = (
  key: DimKey,
  values: () => number[],
  avg: () => number,
  ci: () => [number, number],
): DimensionValues => {
  const v = {
    key,

    get values() {
      return this._values();
    },
    _values: memoize(values),

    get avg() {
      return this._avg();
    },
    _avg: memoize(avg),

    get ci() {
      return this._ci();
    },
    _ci: memoize(ci),

    get kdeData() {
      return this._kdeData(this.values);
    },
    _kdeData: memoize(kde),
  };

  return v as DimensionValues;
};

// Values for all five dimensions. At runtime a five-element array.
// Would be better represented as an object with five properties!
export interface AllDimensionValues extends Array<DimensionValues> {
  dimValues(dimKey: DimKey): undefined | DimensionValues;
}

export const mkAllDimensionValues = (xs: DimensionValues[]): AllDimensionValues => {
  const ret = xs.slice(0) as AllDimensionValues;
  ret.dimValues = (dimKey: DimKey): undefined | DimensionValues => xs.find((d) => d.key === dimKey);
  return ret;
};

// -----------------------------------------------------------------------------
// Dimensions
//
// Contains information about:
//
// - weights of the core dimensions
// - which of the core dimensions is locked
// - which dimension (core or summary) is selected
// - which dimension (core or summayr) is highlighted

export interface Dimensions {
  __dimensionsBrand: any;
}

export interface Weights {
  __weightsBrand: any;
}

const SUMMARY_DIM = mo.js_to_clj({
  key: "public_value",
  summary: true,
});

const CORE_DIMS = mo.js_to_clj([
  {
    key: "benefit",
  },
  {
    key: "social_cohesion",
  },
  {
    key: "quality_of_life",
  },
  {
    key: "ethics",
  },
]);

const DIMENSIONS = mo.concat(mo.vector(SUMMARY_DIM), CORE_DIMS);
const DIMS_KEYS: DimKey[] = mo.set(mo.map((d: any) => mo.get(d, "key"), DIMENSIONS));
const CORE_DIMS_KEYS: CoreDimKey[] = mo.set(mo.map((d: any) => mo.get(d, "key"), CORE_DIMS));
const WEIGHT_ACCURACY = 2;
const WEIGHT_ROUND_DIGITS = WEIGHT_ACCURACY + 5;

const convertIfNeeded = (value: any) => (opts: { js: boolean }) => {
  if (opts != null ? opts.js : void 0) {
    return mo.clj_to_js(value);
  } else {
    return value;
  }
};

export const coreDimensions = _.memoize(convertIfNeeded(CORE_DIMS));
export const dimensions = _.memoize(convertIfNeeded(DIMENSIONS));

export const summaryKey: SummaryDimKey = "public_value";

export const initial = (): Dimensions =>
  mo.hash_map("weights", _equalWeights(CORE_DIMS_KEYS), "locks", mo.set(), "selected", null, "highlighted", null);

export const selected = (dims: Dimensions): null | DimKey => {
  return mo.get(dims, "selected");
};

export const clearSelected = (dims: Dimensions): Dimensions => {
  return clearHighlighted(mo.assoc(dims, "selected", null));
};

export const toggleSelected = (dims: Dimensions, dimKey: DimKey): Dimensions => {
  if (selected(dims) === dimKey) {
    return clearSelected(dims);
  } else {
    return withSelected(dims, dimKey);
  }
};

// XXX: The first argument (_dims) is not used!
export const withSelected = (_dims: Dimensions, dimKey: DimKey): Dimensions => {
  _checkDimKey(dimKey, { allowSummary: true });
  return clearHighlighted(mo.assoc(initial(), "selected", dimKey));
};

export const highlighted = (dims: Dimensions): null | DimKey => {
  return mo.get(dims, "highlighted");
};

export const clearHighlighted = (dims: Dimensions): Dimensions => {
  return mo.assoc(dims, "highlighted", null);
};

export const withHighlighted = (dims: Dimensions, dimKey: null | DimKey): Dimensions => {
  if (dimKey == null) {
    dimKey = null;
  }
  if (dimKey != null) {
    _checkDimKey(dimKey, { allowSummary: true });
  }
  return mo.assoc(dims, "highlighted", dimKey);
};

export const weights = (dims: Dimensions): Weights => {
  return mo.get(dims, "weights");
};

export const weightForKey = (w: Weights, dimKey: DimKey) => {
  return mo.get(w, dimKey);
};

export const weight = (dims: Dimensions, dimKey: DimKey) => {
  return mo.get_in(dims, ["weights", dimKey]);
};

export const withWeights = (dims: Dimensions, weightsByDimKey: any): Dimensions => {
  _checkWeights(weightsByDimKey);
  if (selected(dims) != null) {
    dims = clearSelected(dims);
  }
  return mo.assoc(
    dims,
    "weights",
    mo.zipmap(
      CORE_DIMS_KEYS,
      mo.map((dimKey: DimKey) => {
        return Numbers.round(Numbers.asNumber(weightsByDimKey[dimKey]), WEIGHT_ROUND_DIGITS);
      }, CORE_DIMS_KEYS),
    ),
  );
};

export const locked = (dims: Dimensions, dimKey: CoreDimKey): boolean => {
  return mo.has_key(mo.get(dims, "locks"), dimKey);
};

export const toggleLock = (dims: Dimensions, dimKey: CoreDimKey): Dimensions => {
  _checkDimKey(dimKey);
  const locks = mo.get(dims, "locks");
  const newLocks = mo.get(locks, dimKey) ? mo.disj(locks, dimKey) : mo.conj(locks != null ? locks : mo.set(), dimKey);
  return mo.assoc(dims, "locks", newLocks);
};

export const clearLocks = (dims: Dimensions): Dimensions => {
  return mo.assoc(dims, "locks", mo.set());
};

export const isWeighted = (dims: Dimensions): boolean => {
  dims = mo.assoc(dims, "locks", mo.set());
  const equalized = equalizeWeights(dims);
  return changed(dims, equalized);
};

export const equalizeWeights = (dims: Dimensions, opts: { unlockedOnly?: boolean } = {}) => {
  if (opts.unlockedOnly) {
    const lockedDims = _lockedDimensions(dims);
    if (!mo.is_empty(lockedDims)) {
      const unlocked = _unlockedDimensions(dims);
      let w = _equalWeights(unlocked, _sumWeights(dims, unlocked));
      mo.each(lockedDims, (dim: CoreDimKey) => {
        w = mo.assoc(w, dim, weight(dims, dim));
      });
      return mo.assoc(dims, "weights", w);
    }
  } else {
    dims = clearLocks(dims);
  }
  return mo.assoc(dims, "weights", _equalWeights(CORE_DIMS_KEYS));
};

export const changed = (dims1: Dimensions, dims2: Dimensions, opts: { ignoreLocks?: boolean } = {}): boolean => {
  if (opts.ignoreLocks) {
    return !mo.equals(mo.dissoc(dims1, "locks"), mo.dissoc(dims2, "locks"));
  } else {
    return !mo.equals(dims1, dims2);
  }
};

export const adjustDimensionWeight = (dims: Dimensions, dimKey: CoreDimKey, desiredValue: number): Dimensions => {
  desiredValue = Numbers.asNumber(desiredValue);
  _checkDimKey(dimKey);
  if (locked(dims, dimKey)) {
    return dims;
  }
  if (selected(dims) != null) {
    dims = clearSelected(dims);
  }
  const oldValue = weight(dims, dimKey);
  const diff = desiredValue - oldValue;
  const w = mo.get(dims, "weights");
  const newValues = mo.clj_to_js(w);
  let otherKeys = mo.disj(_unlockedDimensions(dims), dimKey);
  if (mo.is_empty(otherKeys)) {
    return dims;
  }
  const sumOfLocked = _sumWeights(dims, _lockedDimensions(dims));
  const minValue = 0;
  const maxValue = 1.0 - sumOfLocked;
  newValues[dimKey] = Math.max(Math.min(desiredValue, maxValue), minValue);
  otherKeys = mo.clj_to_js(otherKeys);
  let rest = diff;
  while (otherKeys.length > 0) {
    const minKey = _.min(otherKeys, (k: any) => newValues[k]);
    const sub = Math.min(rest / otherKeys.length, newValues[minKey]);
    newValues[minKey] -= sub;
    rest -= sub;
    otherKeys = _.without(otherKeys, minKey);
  }
  for (const k in newValues) {
    const v = newValues[k];
    newValues[k] = Numbers.round(v, WEIGHT_ROUND_DIGITS);
  }
  _checkWeights(newValues);
  return mo.assoc(dims, "weights", mo.js_to_clj(newValues));
};

export const asObject = (dims: Dimensions) => mo.clj_to_js(dims);

export const fromObject = (obj: any): Dimensions => {
  let dims = initial();
  if (obj.weights != null) {
    dims = withWeights(dims, obj.weights);
  }
  if (obj.selected != null && obj.selected !== "") {
    dims = toggleSelected(dims, obj.selected);
  }
  if (obj.locks != null) {
    const ref = obj.locks;
    for (let i = 0, len = ref.length; i < len; i++) {
      const key = ref[i];
      dims = toggleLock(dims, key);
    }
  }
  return dims;
};

const _sumWeights = (dims: Dimensions, dimKeys: CoreDimKey[]): number => {
  const w = mo.get(dims, "weights");
  const weightsOfSelectedDims = mo.map((d: CoreDimKey) => mo.get(w, d), dimKeys);
  return mo.reduce(mo.sum, 0, weightsOfSelectedDims);
};

const _lockedDimensions = (dims: Dimensions) => mo.set(mo.filter((d: CoreDimKey) => locked(dims, d), CORE_DIMS_KEYS));

const _unlockedDimensions = (dims: Dimensions) =>
  mo.set(mo.filter((d: CoreDimKey) => !locked(dims, d), CORE_DIMS_KEYS));

const _equalWeights = (dims: any, sumToDistribute = 1.0) => {
  if (!mo.is_set(dims)) {
    dims = mo.set(dims);
  }
  const w = Numbers.round(sumToDistribute / mo.count(dims), WEIGHT_ROUND_DIGITS);
  return mo.zipmap(
    dims,
    mo.map(() => w, dims),
  );
};

const _checkDimKey = (dimKey: DimKey, opts: { allowSummary?: boolean } = {}) => {
  const allowed = opts.allowSummary ? DIMS_KEYS : CORE_DIMS_KEYS;
  if (!mo.has_key(allowed, dimKey)) {
    throw new Error(`No such dimension '${dimKey}'`);
  }
};

const _checkWeights = (weightsByDimKey: any) => {
  for (const dimKey in weightsByDimKey) {
    const v = weightsByDimKey[dimKey];
    _checkDimKey(dimKey as DimKey);
    if (!Numbers.isDefNum(Numbers.asNumber(v))) {
      throw new Error(`Invalid weight ${v} for '${dimKey}'`);
    }
  }
  const sum = mo.reduce(
    mo.sum,
    mo.map((d: any) => {
      const ref = Numbers.asNumber(weightsByDimKey[d]);
      return ref != null ? ref : 0;
    }, CORE_DIMS_KEYS),
  );

  if (Math.abs(sum - 1) >= Math.pow(10, -WEIGHT_ACCURACY)) {
    throw new Error(`Dimension weights sum up to ${sum}, should be 1.0`);
  }
};
